// Per-portal SMS automation card — live status, next scheduled message and full send history.
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { MessageSquare, ChevronDown, Clock, CheckCircle2, AlertTriangle, CalendarClock, Send, Loader2, Pause, Play, Eye } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const tzAbbr = (tz) => {
  try { return new Intl.DateTimeFormat('en-AU', { timeZone: tz, timeZoneName: 'short' }).formatToParts(new Date()).find((p) => p.type === 'timeZoneName')?.value || ''; }
  catch { return ''; }
};
const fmtWhen = (iso, tz) => {
  if (!iso) return '';
  try {
    const base = new Intl.DateTimeFormat('en-AU', { weekday: 'short', day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit', timeZone: tz || undefined }).format(new Date(iso));
    const ab = tz ? tzAbbr(tz) : '';
    return ab ? `${base} ${ab}` : base;
  } catch { return new Date(iso).toLocaleString(); }
};
const relDay = (iso, tz) => {
  if (!iso) return '';
  try {
    const now = new Date();
    const d = new Date(iso);
    const key = (x) => new Intl.DateTimeFormat('en-CA', { timeZone: tz || undefined, year: 'numeric', month: '2-digit', day: '2-digit' }).format(x);
    const diff = (new Date(key(d)) - new Date(key(now))) / 86400000;
    if (diff <= 0) return 'Today';
    if (diff === 1) return 'Tomorrow';
    return null;
  } catch { return null; }
};

const STATUS_BADGE = {
  delivered: { cls: 'bg-emerald-50 text-emerald-700 border-emerald-200/70', label: 'Delivered', Icon: CheckCircle2 },
  sent: { cls: 'bg-sky-50 text-sky-700 border-sky-200/70', label: 'Sent to carrier', Icon: Send },
  queued: { cls: 'bg-amber-50 text-amber-700 border-amber-200/70', label: 'Awaiting receipt', Icon: Clock },
  failed: { cls: 'bg-rose-50 text-rose-700 border-rose-200/70', label: 'Failed', Icon: AlertTriangle },
  dry_run: { cls: 'bg-slate-100 text-slate-600 border-slate-200/70', label: 'Dry run', Icon: AlertTriangle },
};
const deliveryKey = (s) => {
  if (s.dry_run) return 'dry_run';
  if (s.delivery_status && STATUS_BADGE[s.delivery_status]) return s.delivery_status;
  if (s.status === 'failed') return 'failed';
  if (s.provider_message_id) return 'queued'; // real send, awaiting a delivery receipt
  return 'sent'; // handed to carrier (legacy rows without a tracked message id)
};

const UpcomingBanner = ({ up }) => {
  const [showMsg, setShowMsg] = useState(false);
  const st = up?.state;
  if (st === 'scheduled') {
    const rel = relDay(up.scheduled_at, up.tz);
    return (
      <div className="rounded-xl border border-sky-200/70 bg-sky-50/60 p-3.5" data-testid="obd-sms-upcoming">
        <div className="flex items-start gap-2.5">
          <span className="mt-0.5 grid h-7 w-7 shrink-0 place-items-center rounded-lg bg-sky-100 text-sky-700"><CalendarClock className="w-3.5 h-3.5" /></span>
          <div className="min-w-0 flex-1">
            <p className="text-[11px] font-bold uppercase tracking-wider text-sky-700">Next message{up.one_off ? ' · one-off' : ''}</p>
            <p className="mt-0.5 text-sm font-bold text-[#15133A]" data-testid="obd-sms-upcoming-when">{rel ? `${rel}, ` : ''}{fmtWhen(up.scheduled_at, up.tz)}</p>
            <p className="mt-0.5 text-[12px] text-slate-600">{up.template_name}{up.completion_pct != null ? ` · based on ${up.completion_pct}% complete` : ''}</p>
            {up.note && <p className="mt-1 text-[11px] text-slate-500">{up.note}</p>}
            {up.body && (
              <button type="button" onClick={() => setShowMsg((v) => !v)} data-testid="obd-sms-upcoming-preview-toggle" className="mt-2 inline-flex items-center gap-1 rounded-full border border-sky-300/70 bg-white px-2.5 py-1 text-[11px] font-bold text-sky-800 hover:bg-sky-50">
                <Eye className="w-3 h-3" /> {showMsg ? 'Hide message' : 'Preview message'} <ChevronDown className={`w-3 h-3 transition-transform ${showMsg ? 'rotate-180' : ''}`} />
              </button>
            )}
            {up.body && showMsg && (
              <pre data-testid="obd-sms-upcoming-preview" className="mt-2 whitespace-pre-wrap rounded-xl border border-sky-200 bg-white px-3 py-2.5 text-[12px] leading-relaxed text-slate-700 font-sans">{up.body}</pre>
            )}
          </div>
        </div>
      </div>
    );
  }
  const map = {
    paused: 'Paused for this client — nothing will send until resumed.',
    paused_promised: `Paused until the client's promised date${up?.until ? ` (${up.until})` : ''}. Messaging resumes the morning after.`,
    stopped: `Stopped — ${up?.reason === 'completed' ? 'portal completed' : up?.reason === 'day5_nurture' ? 'moved to nurture after day 5' : up?.reason || 'ended'}.`,
    completed: 'Portal is complete — no further reminders needed.',
    workspace_off: 'Automation is switched off for the whole workspace.',
    no_contact: 'No mobile or portal link on file — add a mobile to enable reminders.',
    inactive_status: 'This portal is no longer in an active reminder stage.',
  };
  return (
    <div className="rounded-xl border border-slate-200 bg-slate-50/70 p-3.5 flex items-start gap-2.5" data-testid="obd-sms-upcoming">
      <span className="mt-0.5 grid h-7 w-7 shrink-0 place-items-center rounded-lg bg-slate-100 text-slate-500"><Clock className="w-3.5 h-3.5" /></span>
      <div className="min-w-0"><p className="text-[11px] font-bold uppercase tracking-wider text-slate-500">No message scheduled</p><p className="mt-0.5 text-[13px] text-slate-600">{map[st] || 'No upcoming message.'}</p></div>
    </div>
  );
};

const SentRow = ({ s }) => {
  const [open, setOpen] = useState(false);
  const b = STATUS_BADGE[deliveryKey(s)] || STATUS_BADGE.queued;
  const when = s.sent_local_date && s.sent_local_time
    ? `${new Intl.DateTimeFormat('en-AU', { weekday: 'short', day: 'numeric', month: 'short' }).format(new Date(s.sent_local_date))}, ${s.sent_local_time}`
    : fmtWhen(s.sent_at, s.client_tz);
  return (
    <li className="py-2.5" data-testid={`obd-sms-sent-${s.id}`}>
      <button type="button" onClick={() => setOpen((o) => !o)} className="flex w-full items-start gap-2.5 text-left">
        <span className="mt-0.5 grid h-6 w-6 shrink-0 place-items-center rounded-lg bg-[#15133A]/5 text-[#15133A]"><Send className="w-3 h-3" /></span>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <span className="text-[13px] font-bold text-slate-900 truncate">{s.template_name}</span>
            <span className={`inline-flex items-center gap-1 rounded-full border px-1.5 py-px text-[9px] font-bold uppercase tracking-wider ${b.cls}`} data-testid={`obd-sms-delivery-${s.id}`}><b.Icon className="w-2.5 h-2.5" />{b.label}</span>
            {s.completion_pct != null && <span className="rounded-full bg-slate-100 px-1.5 py-px text-[10px] font-bold tabular-nums text-slate-600">{s.completion_pct}%</span>}
          </div>
          <p className="mt-0.5 text-[11px] text-slate-500 tabular-nums">{when}{s.slot ? ` · slot ${s.slot}` : ''}</p>
          {s.delivery_status_text && <p className="mt-0.5 text-[11px] text-slate-400">{s.delivery_status_text}</p>}
        </div>
        <ChevronDown className={`mt-1 w-3.5 h-3.5 shrink-0 text-slate-400 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && <pre className="mt-2 ml-8 whitespace-pre-wrap rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5 text-[12px] leading-relaxed text-slate-700 font-sans">{s.body}</pre>}
    </li>
  );
};

export const PortalSmsTimeline = ({ appId, headers, smsEngine, onChanged }) => {
  const se = smsEngine || {};
  const paused = !!se.paused;
  const stopped = !!se.stopped;
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try { const { data } = await axios.get(`${API}/api/onboarding/applications/${appId}/sms-automation/timeline`, { headers }); setData(data); }
    catch { /* ignore */ }
  }, [appId, headers]);
  useEffect(() => { if (open && !data) load(); }, [open, data, load]);

  const toggle = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/api/onboarding/applications/${appId}/sms-automation`, { paused: !paused }, { headers });
      toast.success(!paused ? 'SMS automation paused for this portal' : 'SMS automation resumed');
      setData(null); onChanged && onChanged();
    } catch { toast.error('Could not update automation'); }
    finally { setBusy(false); }
  };

  const count = data?.state?.count ?? se.count ?? 0;
  const statusLine = stopped
    ? `Stopped — ${se.stopped_reason === 'completed' ? 'portal completed' : se.stopped_reason || 'ended'}.`
    : paused ? 'Paused for this client — no automated reminders will send.'
    : `Active${count ? ` · ${count} message${count === 1 ? '' : 's'} sent` : ' · no messages sent yet'}.`;

  return (
    <div className="rounded-2xl border border-slate-200/80 bg-white overflow-hidden" data-testid="obd-sms-automation">
      <div className="flex items-center gap-3 p-4">
        <span className={`h-2.5 w-2.5 shrink-0 rounded-full ${stopped ? 'bg-slate-300' : paused ? 'bg-amber-400' : 'bg-emerald-500'}`} />
        <div className="min-w-0 flex-1">
          <p className="text-sm font-bold text-[#15133A] inline-flex items-center gap-1.5"><MessageSquare className="w-3.5 h-3.5 text-slate-400" /> Portal SMS automation</p>
          <p className="text-[12px] text-slate-500">{statusLine}</p>
        </div>
        <button type="button" onClick={() => setOpen((o) => !o)} data-testid="obd-sms-automation-expand" className="shrink-0 inline-flex items-center gap-1 rounded-xl border border-slate-200 bg-white px-2.5 py-2 text-xs font-bold text-slate-600 hover:bg-slate-50">
          {open ? 'Hide' : 'View'} schedule <ChevronDown className={`w-3.5 h-3.5 transition-transform ${open ? 'rotate-180' : ''}`} />
        </button>
        {!stopped && (
          <button type="button" onClick={toggle} disabled={busy} data-testid="obd-sms-automation-toggle" className={`shrink-0 inline-flex items-center gap-1.5 rounded-xl px-3 py-2 text-xs font-bold disabled:opacity-50 ${paused ? 'bg-[#15133A] text-white hover:bg-[#1E1B52]' : 'border border-slate-200 bg-white text-slate-700 hover:bg-slate-50'}`}>
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : paused ? <Play className="w-3.5 h-3.5" /> : <Pause className="w-3.5 h-3.5" />}{paused ? 'Resume' : 'Pause'}
          </button>
        )}
      </div>
      {open && (
        <div className="border-t border-slate-100 bg-slate-50/40 p-4 space-y-4" data-testid="obd-sms-automation-detail">
          {!data ? <p className="py-6 text-center text-xs font-semibold text-slate-400 inline-flex items-center gap-2 w-full justify-center"><Loader2 className="w-4 h-4 animate-spin" /> Loading schedule…</p> : (
            <>
              <UpcomingBanner up={data.upcoming} />
              <div>
                <p className="mb-2 text-[11px] font-bold uppercase tracking-wider text-slate-400">Sent · {data.sent.length}</p>
                {data.sent.length === 0 ? (
                  <p className="rounded-xl border border-dashed border-slate-200 bg-white px-3 py-4 text-center text-[13px] text-slate-500" data-testid="obd-sms-sent-empty">No automated messages have been sent for this portal yet.</p>
                ) : (
                  <ul className="divide-y divide-slate-100 rounded-xl border border-slate-200 bg-white px-3">{data.sent.map((s) => <SentRow key={s.id} s={s} />)}</ul>
                )}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};
